import React, { useState, useEffect } from "react";
import { useSignalRContext } from "../../../SignalR/useSignalR";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress
} from "@mui/material";
import PositionsRow from "./PositionsRow";
import { getAllSymbols } from "../../../api/symbolApi";
import { getLiquidityProviders, getLiquidityProviderById } from "../../../api/lpApi";
import { getPositions } from "../../../api/positionApi";

export const Position = ({ selectedAccount = null })=>{
    const signalRContext = useSignalRContext();
    const { positionsList, tickData, loadInitialPositions } = signalRContext || {};
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [symbols, setSymbols] = useState([]);
    const [liquidityProviders, setLiquidityProviders] = useState([]);
    const [lpCache, setLpCache] = useState({});
    const [expandedSymbols, setExpandedSymbols] = useState({});
    const [expandedStrategies, setExpandedStrategies] = useState({}); // NEW: Track Level 2 (strategy) expansion
    const [forceUpdateCounter, setForceUpdateCounter] = useState(0);

    const columns= [
        "",                 // Expand icon
        "Symbol",           // TIER 1: Most critical - what you're trading
        "Direction",        // TIER 1: Buy/Sell
        "Volume",           // TIER 1: Position size
        "Entry Price",      // TIER 1: Where you entered
        "Current Price",    // TIER 1: Current market price
        "Profit ($)",       // TIER 1: Dollar PnL - MOST IMPORTANT
        "Profit (Pips)",    // TIER 1: Pip PnL
        "Take Profit",      // TIER 2: Risk management
        "Stop Loss",        // TIER 2: Risk management
        "Opened At",        // TIER 2: Position age
        "AC#",              // TIER 3: Technical/Admin
        "Strategy ID",      // TIER 3: Technical
        "LP Name",          // TIER 3: Technical
        "LP Id",            // TIER 3: Technical
        "Magic ID"          // TIER 3: Technical/Debug
    ];
    // Note: AVG Open Price removed - Entry Price is sufficient for traders

    const fetchSymbols = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Position: No authentication token found for symbols');
                return;
            }

            const symbolsData = await getAllSymbols(token);
            if (symbolsData && symbolsData.length > 0) {
            }
            setSymbols(symbolsData);
        } catch (error) {
            console.error('Position: Error fetching symbols:', error);
        }
    };

    const fetchLPById = async (lpId) => {
        if (lpCache[lpId]) {
            return lpCache[lpId];
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Position: No authentication token for LP fetch');
                return null;
            }

            const lpData = await getLiquidityProviderById(token, lpId);

            setLpCache(prev => ({
                ...prev,
                [lpId]: lpData
            }));

            return lpData;
        } catch (error) {
            console.error('Position: Error fetching LP by ID:', lpId, error);
            return null;
        }
    };

    const fetchLiquidityProviders = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Position: No authentication token found for LPs');
                return;
            }

            const lpData = await getLiquidityProviders(token);

            if (Array.isArray(lpData)) {
                setLiquidityProviders(lpData);
                const cache = {};
                lpData.forEach(lp => {
                    if (lp.id) {
                        cache[lp.id] = lp;
                    }
                });
                setLpCache(cache);
            } else if (lpData && typeof lpData === 'object' && lpData.data && Array.isArray(lpData.data)) {
                // Handle case where API returns {data: [...]}
                setLiquidityProviders(lpData.data);
                // Also populate cache
                const cache = {};
                lpData.data.forEach(lp => {
                    if (lp.id) {
                        cache[lp.id] = lp;
                    }
                });
                setLpCache(cache);
            } else {
                console.warn('Position: LP data is not an array, setting empty array');
                setLiquidityProviders([]);
            }
        } catch (error) {
            console.error('Position: Error fetching liquidity providers:', error);
            setLiquidityProviders([]); // Set empty array on error
        }
    };

    // Fetch symbols, LPs, and initial positions on component mount
    useEffect(() => {
        console.log('🔄 Position component mounted/remounted - loading data...');
        console.log('Current positions in context:', positionsList?.length || 0);

        fetchSymbols();
        fetchLiquidityProviders();

        // CRITICAL FIX: Only load positions from API if there are NO positions in context
        // This prevents overwriting real-time SignalR data when switching tabs
        // Only reload from API on fresh page load when positionsList is empty
        if (loadInitialPositions && (!positionsList || positionsList.length === 0)) {
            console.log('📞 No positions in context - calling loadInitialPositions...');
            loadInitialPositions();
        } else {
            console.log('✅ Using existing positions from SignalR context (tab switch)');
        }
    }, []); // Run only once on mount, don't re-run when dependencies change

    const toggleSymbolExpansion = (symbol) => {
        setExpandedSymbols(prev => {
            const newState = {
                ...prev,
                [symbol]: !prev[symbol]
            };
            console.log('🔄 Toggle Level 1 (Symbol) expansion:', {
                symbol,
                previousState: prev,
                newState,
                isNowExpanded: newState[symbol]
            });
            return newState;
        });
    };

    // NEW: Toggle function for Level 2 (Strategy) expansion
    const toggleStrategyExpansion = (strategyKey) => {
        setExpandedStrategies(prev => {
            const newState = {
                ...prev,
                [strategyKey]: !prev[strategyKey]
            };
            console.log('🔄 Toggle Level 2 (Strategy) expansion:', {
                strategyKey,
                previousState: prev,
                newState,
                isNowExpanded: newState[strategyKey]
            });
            return newState;
        });
    };

    const rawPositions = (positionsList && Array.isArray(positionsList)) ? positionsList : [];


    // Ensure rawPositions is an array
    const safeRawPositions = Array.isArray(rawPositions) ? rawPositions : [];

    if (safeRawPositions.length > 0) {
        console.log('📦 Raw Positions from Context:', {
            count: safeRawPositions.length,
            symbols: safeRawPositions.map(p => p.symbol),
            positions: safeRawPositions.map(p => ({
                symbol: p.symbol,
                strategyCode: p.strategyCode,
                isFlat: p.isFlat,
                accountNumber: p.accountNumber || p.AccountNumber
            }))
        });
    }

    // Filter out positions where isFlat is true (only show non-flat positions)
    // Also filter by selected account number if provided
    const displayPositions = safeRawPositions.filter(position => {
        const isFlat = position.isFlat;
        const shouldShow = isFlat === false || isFlat === undefined || isFlat === null;

        // If selectedAccount is provided, also filter by account number
        if (selectedAccount) {
            const positionAccountNumber = position.accountNumber || position.AccountNumber;
            // Convert both to strings for comparison to handle type mismatch
            const selectedAccountStr = String(selectedAccount);
            const positionAccountStr = String(positionAccountNumber);
            return shouldShow && positionAccountStr === selectedAccountStr;
        }

        return shouldShow;
    });

    // Note: If displayPositions.length === 0 and safeRawPositions.length > 0,
    // it means all positions are flat (closed). This is normal behavior - no warning needed.

    // Group positions by symbol
    const groupedPositions = {};
    displayPositions.forEach(position => {
        const symbol = position?.symbol || 'Unknown';
        if (!groupedPositions[symbol]) {
            groupedPositions[symbol] = [];
        }
        groupedPositions[symbol].push(position);
    });

    // Debug logging to see what symbols are grouped
    console.log('🔍 Grouped Positions:', {
        totalDisplayPositions: displayPositions.length,
        symbols: Object.keys(groupedPositions),
        groupCount: Object.keys(groupedPositions).length,
        details: Object.entries(groupedPositions).map(([symbol, positions]) => ({
            symbol,
            count: positions.length,
            directions: positions.map(p => p.direction)
        }))
    });


    // Calculate aggregated data for parent rows
    const calculateAggregatedData = (positions, symbol) => {
        if (!positions || positions.length === 0) return null;

        // Transform positions first
        const transformedPositions = positions.map(p => transformPositionData(p));

        // Calculate net quantity (Buy - Sell)
        const buyVolume = transformedPositions
            .filter(p => p.direction === 'Buy')
            .reduce((sum, p) => sum + (p.volume || 0), 0);

        const sellVolume = transformedPositions
            .filter(p => p.direction === 'Sell')
            .reduce((sum, p) => sum + (p.volume || 0), 0);

        const netQuantity = Math.abs(buyVolume - sellVolume);

        // Determine overall direction based on which volume is greater
        const overallDirection = buyVolume >= sellVolume ? 'Buy' : 'Sell';

        // Get LP info from first position (assuming same symbol = same LP)
        const firstPosition = transformedPositions[0];
        const lpName = firstPosition?.lpName || '';
        const lpId = firstPosition?.lpId || '';

        // Calculate total lot count across all child positions
        const totalLotCount = transformedPositions.reduce((sum, p) => sum + (p.lots?.length || 0), 0);

        // Get earliest and latest timestamps
        const timestamps = transformedPositions
            .map(p => p.openedAtRaw)
            .filter(t => t)
            .map(t => new Date(t).getTime())
            .filter(t => !isNaN(t));

        const firstOpenedAt = timestamps.length > 0 ? formatTimestamp(new Date(Math.min(...timestamps))) : '--';
        const lastOpenedAt = timestamps.length > 0 ? formatTimestamp(new Date(Math.max(...timestamps))) : '--';
        // Extract only time portion (HH:MM:SS) and shorten to HH:MM for parent rows
        const firstTime = firstOpenedAt !== '--' ? firstOpenedAt.split(' ')[1]?.substring(0, 5) || '--' : '--';
        const lastTime = lastOpenedAt !== '--' ? lastOpenedAt.split(' ')[1]?.substring(0, 5) || '--' : '--';
        const openedAtRange = timestamps.length > 0 ? `${firstTime}-${lastTime}` : '--';

        return {
            magicId: `${symbol}-AGG`, // NEW: Aggregation identifier
            id: '', // Empty string, not N/A
            symbol: symbol,
            lpName: lpName,
            lpId: lpId,
            accountNumber: '--', // Show dash for parent row
            direction: overallDirection,
            volume: netQuantity,
            volumeWithCount: `${netQuantity.toFixed(2)} (${totalLotCount})`, // NEW: Net volume with total lot count
            openPrice: '--', // Show dash
            entryPrice: '--', // Show dash
            currentPrice: '--', // Show dash
            profit: 0, // Will be calculated from child positions in PositionsRow
            profitPips: 0, // Will be calculated from child positions in PositionsRow
            openedAt: openedAtRange, // NEW: Time range for aggregated positions
            takeProfit: '--',
            stopLoss: '--',
            isParent: true, // Flag to identify parent rows
            childCount: positions.length,
            childPositions: transformedPositions // Pass child positions for profit calculation
        };
    };
    
    
    
    // Force re-render when positionsList updates with strategyId values
    useEffect(() => {
        if (positionsList && positionsList.length > 0) {
            // Check if any position has a non-zero strategyId
            const hasStrategyId = positionsList.some(position => {
                const strategyId = position?.strategyId || position?.StrategyId || position?.strategyID || position?.strategy_id || 0;
                return strategyId !== 0 && strategyId !== null && strategyId !== undefined;
            });

            // Force re-render when strategyId becomes available
            if (hasStrategyId) {
                setForceUpdateCounter(prev => prev + 1);
            }
        }
    }, [positionsList]);

    // Listen for CalculatedPositionUpdate events and force re-render
    useEffect(() => {
        const handleCalculatedPositionUpdate = (event) => {
            // Force re-render when calculated position update event fires
            setForceUpdateCounter(prev => prev + 1);
        };

        window.addEventListener('calculatedPositionUpdated', handleCalculatedPositionUpdate);

        return () => {
            window.removeEventListener('calculatedPositionUpdated', handleCalculatedPositionUpdate);
        };
    }, []);

    // No longer need API event listeners - using SignalR for real-time updates

    // Manual test function to simulate SignalR position update
    const testSignalRUpdate = () => {
        
        // Test: Custom event trigger
        window.dispatchEvent(new CustomEvent('positionUpdated', { 
            detail: [{ 
                id: 'test-123', 
                symbolName: 'EURUSD', 
                tradeSide: 'Buy', 
                volume: 100000,
                entryPrice: 1.0850,
                currentPrice: 1.0855
            }] 
        }));
        
        // Test: Direct position update simulation
        // This simulates what should happen when SignalR receives a PositionReport
        const mockPosition = {
            id: 'test-456',
            symbolName: 'GBPUSD',
            tradeSide: 'Sell',
            volume: 50000,
            entryPrice: 1.2650,
            currentPrice: 1.2645
        };
        
        // Manually trigger the position update
        window.dispatchEvent(new CustomEvent('positionAdded', { 
            detail: [mockPosition] 
        }));
    };

    // Generate Magic ID for position identification
    // Format: {StrategyId}-{AccLast2}-{Symbol}-{Direction}-{Timestamp}
    // Example: "1-34-EURUSD-Buy-1638" or "MAN-34-EURUSD-Sell-1640"
    const generateMagicId = (position) => {
        // Strategy ID or "MAN" for manual orders
        const strategyId = position?.strategyId || position?.StrategyId || position?.strategyID || position?.strategy_id || 0;
        const strategyPart = strategyId === 0 ? 'MAN' : strategyId.toString();

        // Last 2 digits of account number
        const accountNumber = position?.accountNumber || position?.AccountNumber || '';
        const accLast2 = accountNumber ? String(accountNumber).slice(-2) : '00';

        // Symbol
        const symbol = position?.symbol || 'UNK';

        // Direction based on netVolume
        const netVolume = position?.netVolume || 0;
        const direction = netVolume >= 0 ? 'Buy' : 'Sell';

        // Last 4 digits of createdAt timestamp (or use current time as fallback)
        const createdAt = position?.createdAt || position?.updatedAt || Date.now().toString();
        const timestampPart = createdAt.toString().replace(/\D/g, '').slice(-4) || '0000';

        return `${strategyPart}-${accLast2}-${symbol}-${direction}-${timestampPart}`;
    };

    // Format timestamp for display
    // Converts ISO timestamp to readable format: "01/15 14:23:45"
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '--';
        try {
            const date = new Date(timestamp);
            // Check if valid date
            if (isNaN(date.getTime())) return '--';

            return date.toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
        } catch (e) {
            console.warn('Error formatting timestamp:', timestamp, e);
            return timestamp;
        }
    };

    // Generate Lot ID for individual lot/order
    // Format: LOT-{StrategyId}-{LotIndex}-{Timestamp}
    const generateLotId = (strategyId, lotIndex, timestamp) => {
        const strategyPart = strategyId === 0 ? 'MAN' : strategyId;
        const timestampPart = timestamp ? timestamp.toString().replace(/\D/g, '').slice(-6) : '000000';
        return `LOT-${strategyPart}-${lotIndex}-${timestampPart}`;
    };

    // Sort lots by timestamp (newest first)
    const sortLotsByTimestamp = (lots) => {
        if (!lots || !Array.isArray(lots)) return [];

        return [...lots].sort((a, b) => {
            const timeA = new Date(a.time || a.createdAt || a.timestamp || 0).getTime();
            const timeB = new Date(b.time || b.createdAt || b.timestamp || 0).getTime();
            return timeB - timeA; // Descending: newest first
        });
    };

    // Transform SignalR position data to match the expected format
    const transformPositionData = (position) => {

        // Safety check - ensure position exists
        if (!position) {
            return {
                id: 0,
                symbol: '',
                direction: '',
                volume: 0,
                openPrice: 0,
                entryPrice: 0,
                currentPrice: 0,
                profit: 0,
                profitPips: 0,
                isFlat: true,
                totalExposure: 0,
                totalVolume: 0,
                netVolume: 0,
                strategyCode: '',
                createdAt: '',
                updatedAt: '',
                lots: [],
                takeProfit: '',
                stopLoss: ''
            };
        }


        // Determine direction based on netVolume (primary) and totalExposure (fallback)
        let direction = 'Buy'; // Default to Buy
        const netVolume = position?.netVolume || 0;
        const totalExposure = position?.totalExposure || 0;

        if (netVolume > 0) {
            direction = 'Buy';
        } else if (netVolume < 0) {
            direction = 'Sell';
        } else if (totalExposure > 0) {
            direction = 'Buy';
        } else if (totalExposure < 0) {
            direction = 'Sell';
        } else {
            // If both are 0, check if there are any lots to determine direction
            if (position?.lots && position.lots.length > 0) {
                const buyLots = position.lots.filter(lot => lot?.direction === 'Buy').length;
                const sellLots = position.lots.filter(lot => lot?.direction === 'Sell').length;
                if (buyLots > sellLots) {
                    direction = 'Buy';
                } else if (sellLots > buyLots) {
                    direction = 'Sell';
                } else {
                    // If equal, default to Buy
                    direction = 'Buy';
                }
            }
        }

        // Get Entry Price from the LAST lot's price in the lots array
        let entryPrice = 0;
        if (position?.lots && position.lots.length > 0) {
            const lastLot = position.lots[position.lots.length - 1];
            entryPrice = lastLot?.price || 0;
        }

        // Get current price from tick data
        let currentPrice = '';

        if (tickData && Array.isArray(tickData) && tickData.length > 0) {
            const tickIndex = tickData.findIndex((item) => item?.symbolName === position?.symbol);
            if (tickIndex > -1) {
                const quote = tickData[tickIndex];
                // Use ask price for Buy positions, bid price for Sell positions
                currentPrice = direction === 'Buy' ? quote?.ask : quote?.bid;
            }
        }

        // Calculate quantity using contract size

        // Safely find symbol info with array check
        let symbolInfo = null;
        if (Array.isArray(symbols) && symbols.length > 0) {
            symbolInfo = symbols.find(s => s.name === position?.symbol );
        }

        // Get LP information from symbol and cache
        let lpName = '';
        let lpId = '';

        if (symbolInfo?.lpId) {
            lpId = symbolInfo.lpId;

            // CRITICAL FIX: Only use cached LP data, DO NOT fetch inside transform function
            // This was causing 15+ API calls on every render
            if (lpCache[lpId]) {
                lpName = lpCache[lpId]?.name || '';
            } else {
                // LP not in cache - show ID only, will be populated on next render after fetchLiquidityProviders completes
                lpName = `LP ${lpId}`; // Temporary fallback until cache is populated
            }
        }

        // Use default contract sizes for common symbols if not found in symbols API
        const defaultContractSizes = {
            'XAUUSD': 100,
            'EURUSD': 100000,
            'GBPUSD': 100000,
            'USDJPY': 100000,
            'AUDUSD': 100000,
            'USDCAD': 100000,
            'NZDUSD': 100000,
            'USDCHF': 100000
        };

        const contractSize = symbolInfo?.contractSize || defaultContractSizes[position?.symbol] || 1;
        const calculatedQuantity = Math.abs(position?.netVolume || 0) / contractSize;

        // Format quantity to show more decimal places for small values
        const formattedQuantity = calculatedQuantity < 0.01 ? calculatedQuantity.toFixed(6) : calculatedQuantity.toFixed(2);

        // Get strategyId from multiple possible property names
        const strategyIdValue = position?.strategyId !== undefined ? position.strategyId
                              : position?.StrategyId !== undefined ? position.StrategyId
                              : position?.strategyID !== undefined ? position.strategyID
                              : position?.strategy_id !== undefined ? position.strategy_id
                              : 0;

        const transformedData = {
            magicId: generateMagicId(position), // NEW: Magic ID for unique identification
            id: strategyIdValue, // Use numeric strategyId (0, 1, 2, etc.)
            symbol: position?.symbol || '',
            lpName: lpName, // LP Name from symbols and LP data
            lpId: lpId, // LP Id from symbol data
            accountNumber: position?.accountNumber || position?.AccountNumber || '', // Account Number
            direction: direction,
            volume: parseFloat(formattedQuantity), // Position size calculated with contract size
            volumeWithCount: `${formattedQuantity} (${position?.lots?.length || 0})`, // NEW: Volume with order count
            openPrice: position?.averagePrice || 0, // AVG Open Price - use averagePrice
            entryPrice: entryPrice, // Entry Price - use last lot's price
            currentPrice: currentPrice, // Current Price - from tick data
            isFlat: position?.isFlat || false,
            totalExposure: position?.totalExposure || 0,
            totalVolume: position?.totalVolume || 0, // Total volume of all trades
            netVolume: position?.netVolume || 0, // Net position (can be positive/negative)
            strategyId: strategyIdValue, // Numeric Strategy ID
            strategyCode: position?.strategyCode || position?.StrategyCode || '', // Strategy Code (like "DB0")
            createdAt: position?.createdAt || '',
            updatedAt: position?.updatedAt || '',
            openedAt: formatTimestamp(position?.createdAt), // NEW: Formatted timestamp for display
            openedAtRaw: position?.createdAt || '', // NEW: Raw timestamp for sorting
            lots: position?.lots || [],
            takeProfit: position?.takeProfit || '',
            stopLoss: position?.stopLoss || '',
            conversionRateToUsd: position?.conversionRateToUsd || 1 // Conversion rate for profit calculation
        };

        return transformedData;
    };
    return(
        <Box padding={0} margin={0} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <TableContainer component={Paper} sx={{
                boxShadow: "none",
                overflowX: "auto",  // Horizontal scrollbar
                overflowY: "auto",  // Vertical scrollbar
                margin: '0 !important',
                padding: '0 !important',
                flex: 1,
                height: '100%',
                maxHeight: 'calc(100vh - 200px)', // Ensure scrolling with max height
                // Firefox scrollbar styling
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(6, 182, 212, 0.5) rgba(0, 0, 0, 0.2)',
                // Chromium scrollbar styling (Chrome, Edge, Opera)
                '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(6, 182, 212, 0.5)',
                    borderRadius: '4px',
                    '&:hover': {
                        backgroundColor: 'rgba(6, 182, 212, 0.7)',
                    },
                },
            }}>
                <Table
                        sx={{
                            minWidth: '1310px', // Force horizontal scrollbar: 16 columns total width
                            marginTop: '0 !important',
                            borderSpacing: "0px 0px",
                            borderCollapse: "separate",
                            '& .MuiTableCell-root': {
                                padding: '4px 8px',
                                fontSize: '11px',
                                height: 24,
                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                backgroundColor: 'rgba(6, 182, 212, 0.12)',
                                fontWeight: 600,
                                fontSize: '11px',
                                height: 26,
                                color: '#06b6d4',
                                padding: '4px 8px',
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                            },
                            '& .MuiTableRow-root': {
                                height: 24,
                            },
                        }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    "& .MuiTableCell-root": {
                                    padding: "0px 10px",
                                    borderBottom: "none",
                                  },
                                }}
                            >
                                {columns.map((header, index)=>{
                                    // Define alignment and width for each column to match data rows
                                    const getColumnStyles = (columnName, colIndex) => {
                                        // Column 0: Expand Icon
                                        if (colIndex === 0) return { align: 'left', sx: { width: '40px', minWidth: '40px', maxWidth: '120px' } };

                                        // Columns with specific alignments and widths
                                        const columnConfig = {
                                            "Symbol": { align: 'left', sx: { minWidth: '70px', maxWidth: '90px' } },
                                            "Direction": { align: 'left', sx: { minWidth: '50px', maxWidth: '60px' } },
                                            "Volume": { align: 'right', sx: { minWidth: '80px', maxWidth: '100px' } },
                                            "Entry Price": { align: 'right', sx: { minWidth: '80px', maxWidth: '100px' } },
                                            "Current Price": { align: 'right', sx: { minWidth: '80px', maxWidth: '100px' } },
                                            "Profit ($)": { align: 'right', sx: { minWidth: '70px', maxWidth: '90px' } },
                                            "Profit (Pips)": { align: 'right', sx: { minWidth: '70px', maxWidth: '90px' } },
                                            "Take Profit": { align: 'right', sx: { minWidth: '70px', maxWidth: '90px' } },
                                            "Stop Loss": { align: 'right', sx: { minWidth: '70px', maxWidth: '90px' } },
                                            "Opened At": { align: 'left', sx: { minWidth: '110px', maxWidth: '130px' } },
                                            "AC#": { align: 'left', sx: { minWidth: '70px', maxWidth: '90px' } },
                                            "Strategy ID": { align: 'left', sx: { minWidth: '60px', maxWidth: '80px' } },
                                            "LP Name": { align: 'left', sx: { minWidth: '100px', maxWidth: '120px' } },
                                            "LP Id": { align: 'left', sx: { minWidth: '60px', maxWidth: '80px' } },
                                            "Magic ID": { align: 'left', sx: { minWidth: '120px', maxWidth: '140px' } }
                                        };

                                        return columnConfig[columnName] || { align: 'left', sx: {} };
                                    };

                                    const colStyle = getColumnStyles(header, index);

                                    return(
                                    <TableCell key={index} align={colStyle.align} sx={colStyle.sx}>
                                        <Typography color="text.tableheader">
                                            {header}
                                        </Typography>
                                    </TableCell>
                                    )
                                })}

                            </TableRow>
                        </TableHead>
                        <TableBody key={`tbody-${displayPositions.length}-${Object.keys(groupedPositions).length}`}>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={16}>
                                        <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                                            <CircularProgress size={24} />
                                            <Typography sx={{ ml: 2 }}>Loading positions...</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={16}>
                                        <Box display="flex" justifyContent="center" py={2}>
                                            <Typography color="error">{error}</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (Object.keys(groupedPositions).length > 0) ? (
                                // Render grouped positions by symbol
                                Object.keys(groupedPositions).map((symbol, symbolIdx) => {
                                    const positions = groupedPositions[symbol];
                                    const isExpanded = expandedSymbols[symbol] || false;
                                    const aggregatedData = calculateAggregatedData(positions, symbol);

                                    // Find symbol info for this symbol (contains contractSize and digits)
                                    const symbolInfo = symbols.find(s => s.name === symbol);

                                    console.log(`🎨 Rendering parent row ${symbolIdx}:`, {
                                        symbol,
                                        positionsCount: positions.length,
                                        isExpanded
                                    });

                                    return (
                                        <React.Fragment key={`symbol-group-${symbol}-${symbolIdx}`}>
                                            {/* Parent Row */}
                                            <PositionsRow
                                                id={aggregatedData.id}
                                                symbolName={aggregatedData.symbol}
                                                tradeSide={aggregatedData.direction}
                                                volume={aggregatedData.volume}
                                                entryPrice={aggregatedData.entryPrice}
                                                data={aggregatedData}
                                                rowIdx={symbolIdx}
                                                columnVisibility={columns.reduce((acc, col) => ({ ...acc, [col]: true }), {})}
                                                isParent={true}
                                                isExpanded={isExpanded}
                                                onToggleExpand={() => toggleSymbolExpansion(symbol)}
                                                childCount={positions.length}
                                                symbolInfo={symbolInfo}
                                                magicId={aggregatedData.magicId}
                                                openedAt={aggregatedData.openedAt}
                                            />

                                            {/* Child Rows (Expanded) - Level 2: Strategy Positions */}
                                            {isExpanded && positions.map((position, posIdx) => {
                                                // Transform position data
                                                const transformedData = transformPositionData(position);

                                                // Create unique key for this strategy position
                                                const strategyKey = `${symbol}-${transformedData.id}-${transformedData.magicId}`;
                                                const isStrategyExpanded = expandedStrategies[strategyKey] || false;
                                                const hasLots = transformedData.lots && transformedData.lots.length > 0;

                                                return (
                                                    <React.Fragment key={`strategy-${strategyKey}-${posIdx}`}>
                                                        {/* Level 2: Strategy Position Row */}
                                                        <PositionsRow
                                                            key={`child-${symbol}-${position?.strategyId || position?.strategyCode || posIdx}`}
                                                            id={transformedData.id}
                                                            symbolName={transformedData.symbol}
                                                            tradeSide={transformedData.direction}
                                                            volume={transformedData.volume}
                                                            entryPrice={transformedData.entryPrice}
                                                            data={transformedData}
                                                            rowIdx={posIdx}
                                                            columnVisibility={columns.reduce((acc, col) => ({ ...acc, [col]: true }), {})}
                                                            isParent={false}
                                                            isChild={true}
                                                            isExpanded={isStrategyExpanded}
                                                            onToggleExpand={() => toggleStrategyExpansion(strategyKey)}
                                                            hasLots={hasLots}
                                                            symbolInfo={symbolInfo}
                                                            magicId={transformedData.magicId}
                                                            openedAt={transformedData.openedAt}
                                                        />

                                                        {/* Level 3: Individual Lot Rows (Grandchildren) */}
                                                        {isStrategyExpanded && hasLots && (() => {
                                                            const sortedLots = sortLotsByTimestamp(transformedData.lots);

                                                            // Show empty state if no lots available
                                                            if (!sortedLots || sortedLots.length === 0) {
                                                                return (
                                                                    <TableRow key={`no-lots-${strategyKey}`}>
                                                                        <TableCell colSpan={16} sx={{ paddingLeft: '80px' }}>
                                                                            <Typography sx={{ color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>
                                                                                No individual lots available
                                                                            </Typography>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            }

                                                            return sortedLots.map((lot, lotIdx) => {
                                                                // Generate unique lot ID
                                                                const lotId = generateLotId(
                                                                    transformedData.id,
                                                                    lotIdx,
                                                                    lot.time || lot.createdAt || lot.timestamp
                                                                );

                                                                // Calculate lot volume in lots (convert from base units)
                                                                const contractSize = symbolInfo?.contractSize || 100000;
                                                                const lotVolume = (lot.volume || 0) / contractSize;
                                                                const formattedLotVolume = lotVolume < 0.01 ? lotVolume.toFixed(6) : lotVolume.toFixed(2);

                                                                // Transform lot data to match position structure
                                                                const lotData = {
                                                                    magicId: lotId,
                                                                    id: transformedData.id,
                                                                    symbol: transformedData.symbol,
                                                                    lpName: transformedData.lpName,
                                                                    lpId: transformedData.lpId,
                                                                    accountNumber: transformedData.accountNumber,
                                                                    direction: lot.direction || transformedData.direction,
                                                                    volume: parseFloat(formattedLotVolume),
                                                                    volumeWithCount: formattedLotVolume, // No count for individual lots
                                                                    openPrice: lot.price || 0,
                                                                    entryPrice: lot.price || 0,
                                                                    openedAt: formatTimestamp(lot.time || lot.createdAt || lot.timestamp),
                                                                    takeProfit: transformedData.takeProfit,
                                                                    stopLoss: transformedData.stopLoss,
                                                                    conversionRateToUsd: transformedData.conversionRateToUsd,
                                                                    isLot: true // Flag to identify lot rows
                                                                };

                                                                return (
                                                                    <PositionsRow
                                                                        key={`lot-${strategyKey}-${lotIdx}`}
                                                                        id={transformedData.id}
                                                                        symbolName={transformedData.symbol}
                                                                        tradeSide={lot.direction || transformedData.direction}
                                                                        volume={parseFloat(formattedLotVolume)}
                                                                        entryPrice={lot.price || 0}
                                                                        data={lotData}
                                                                        rowIdx={lotIdx}
                                                                        columnVisibility={columns.reduce((acc, col) => ({ ...acc, [col]: true }), {})}
                                                                        isParent={false}
                                                                        isChild={true}
                                                                        isGrandchild={true}
                                                                        symbolInfo={symbolInfo}
                                                                        magicId={lotId}
                                                                        openedAt={lotData.openedAt}
                                                                    />
                                                                );
                                                            });
                                                        })()}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <TableRow
                                    sx={{
                                        "& .MuiTableCell-root": {
                                            padding: "0px 10px",
                                            borderBottom: "none",
                                        },
                                    }}>
                                    <TableCell colSpan={16}>
                                        <Box display="flex" justifyContent="center" py={4}>
                                            <Typography color="text.secondary" variant="body1">
                                                No Data Found!
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
            </TableContainer>
            
        </Box>
    )
};